import { Component } from 'react';
import styled from 'styled-components';
import flatten from 'lodash/flatten';
import isEqual from 'lodash/isEqual';
import keys from 'lodash/keys';
import {
  spacing,
  size,
  Media,
  MediaBody,
  ButtonReset,
} from 'ui';
import { formatDate } from 'utils';
import AuthorTime from 'components/AuthorTime';
import MagnifiableAttachment from 'components/MagnifiableAttachment';
import Attachment, { MessageAttachments } from 'components/AttachmentNew';
import { CardElement, CardElementContent, CardElementHeader } from '@tymate/sergic';

const MessageBody = styled.div`
  white-space: pre-wrap;
`;

const MessagesContainer = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column-reverse;
  padding: ${spacing()};
`;

const MessagesGroup = styled(MediaBody)`
  padding-left: 0;
`;

const Message = styled(CardElement)`
  + * {
    margin-top: 4px;
  }

  line-height: 1.2;
  text-align: inherit;
`;

const GroupWrapper = styled.div`
  + div {
    margin-bottom: ${spacing(2)};
  }
`;

const Group = styled.div`
  + * {
    margin-top: ${spacing(2)};
  }
`;

const GroupTimestamp = styled.p`
  text-align: center;
  font-size: ${size(-1)};
  color: ${({ theme }) => theme.textLight};
  text-transform: uppercase;
  margin: 0 0 ${spacing()};
`;

const imagesAreLoaded = imagesIds => {
  return (
    keys(imagesIds)
      .map(id => imagesIds[id])
      .filter(status => !Boolean(status)).length === 0
  );
};

class ConversationMessages extends Component {
  state = {
    isPristine: true,
    imagesLoaded: {},
  };

  componentDidUpdate(prevProps, prevState) {
    const { messages } = this.props;
    const { imagesLoaded, isPristine } = this.state;

    if (!isEqual(messages, prevProps.messages)) {
      this.populateImagesLoaded();
    }

    if (!imagesAreLoaded(imagesLoaded) && isPristine) {
      window.scrollTo(0, 999999);
      // this.scrollbars.scrollToBottom();
    }
  }

  populateImagesLoaded() {
    const { messages } = this.props;

    let imagesLoaded = {};

    /**
     * Extract all images from messages, then set there loading status to false
     */
    (
      flatten(
        flatten(messages.map(({ authors }) => authors)).map(
          ({ messages }) => messages,
        ) || [],
      ) || []
    )
      .filter(({ picture }) => Boolean(picture))
      .map(({ picture }) => picture.id)
      .forEach(id => {
        imagesLoaded = {
          ...imagesLoaded,
          [id]: false,
        };
      });

    this.setState({ imagesLoaded });
  }

  handleStartScroll = () => {
    this.setState({ isPristine: false });
  };

  handleLoadImage = id => {
    const { imagesLoaded } = this.state;

    this.setState({
      imagesLoaded: {
        ...imagesLoaded,
        [id]: true,
      },
    });
  };

  render() {
    const { messages } = this.props;

    return (
      <MessagesContainer>
        {(messages || []).map(({ day, authors }) => (
          <GroupWrapper key={day}>
            <GroupTimestamp>{formatDate(day, 'd MMMM yyyy')}</GroupTimestamp>

            {authors.map(({ author, messages, fromMe }, key) => (
              <Group key={key}>
                <Media>
                  <MessagesGroup>
                    {messages.map(
                      ({ id, body, createdAt, picture, attachments }) => (
                        <Message key={id}>
                          <CardElementHeader>
                            <AuthorTime
                              author={author}
                              timestamp={createdAt}
                              small
                            />
                          </CardElementHeader>

                          <CardElementContent>
                            <MessageBody>{body}</MessageBody>

                            {attachments.length > 0 && (
                              <MessageAttachments>
                                {attachments.map(attachment => (
                                  <MagnifiableAttachment
                                    key={attachment.id}
                                    {...attachment}
                                  >
                                    {handleRequestOpen => (
                                      <ButtonReset onClick={handleRequestOpen}>
                                        <Attachment
                                          {...attachment}
                                          shouldHavePreview={
                                            attachments.filter(
                                              ({ sourceContentType }) =>
                                                sourceContentType.indexOf(
                                                  'image',
                                                ) === -1,
                                            ).length === 0
                                          }
                                        />
                                      </ButtonReset>
                                    )}
                                  </MagnifiableAttachment>
                                ))}
                              </MessageAttachments>
                            )}
                          </CardElementContent>
                        </Message>
                      ),
                    )}
                  </MessagesGroup>
                </Media>
              </Group>
            ))}
          </GroupWrapper>
        ))}
      </MessagesContainer>
    );
  }
}

export default ConversationMessages;
